import { Outlet, Navigate, useParams, useLocation } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
// components
import { useEffect, useState, useRef } from 'react';
import useSettings from '../hooks/useSettings';
import { get } from '../api/requests';
import { endpoints } from '../api/endpoints';

// ----------------------------------------------------------------------
import LoadingScreen from '../components/LoadingScreen';
import EventExpired from 'src/pages/Error/EventExpired';
import { checkIfPackageExpired } from 'src/helpers/functions';
import { decrypt } from 'src/helpers/constants';
import { useSnackbar } from 'notistack';

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  left: 0,
  lineHeight: 0,
  width: '100%',
  position: 'absolute',
  padding: theme.spacing(3, 3, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5, 0),
  },
}));

// Helper to retrieve and validate cached event details for a specific slug
const getCachedDataForSlug = (targetSlug) => {
  try {
    const d = localStorage.getItem('arsh:event');
    if (!d) return null;
    const parsed = JSON.parse(d);
    if (!parsed || typeof parsed !== 'object') return null;

    const slugStr = String(targetSlug || '').trim().toLowerCase();
    if (!slugStr) return null;

    const matches =
      (parsed.eventRows?.[0]?.event_code &&
        String(parsed.eventRows[0].event_code).trim().toLowerCase() === slugStr) ||
      (parsed.eventRows?.[0]?.event_name &&
        String(parsed.eventRows[0].event_name).trim().toLowerCase() === slugStr) ||
      (parsed.event && String(parsed.event).trim().toLowerCase() === slugStr) ||
      (parsed.short_code && String(parsed.short_code).trim().toLowerCase() === slugStr) ||
      (parsed.event_code && String(parsed.event_code).trim().toLowerCase() === slugStr) ||
      (parsed.id && String(parsed.id).trim().toLowerCase() === slugStr) ||
      (parsed.title && String(parsed.title).trim().toLowerCase() === slugStr) ||
      (parsed.name && String(parsed.name).trim().toLowerCase() === slugStr) ||
      (parsed.event_name && String(parsed.event_name).trim().toLowerCase() === slugStr) ||
      (parsed.slug && String(parsed.slug).trim().toLowerCase() === slugStr);

    return matches ? parsed : null;
  } catch (e) {
    return null;
  }
};

const checkRowExpired = (row) => {
  try {
    const pkgRow = row?.eventRows?.[0] || row;
    const x41 = pkgRow?.x41;
    if (x41) {
      return checkIfPackageExpired(decrypt(x41))?.isExpired === true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

// ----------------------------------------------------------------------

export default function LogoOnlyLayout({ blocking }) {
  const params = useParams();
  const location = useLocation();
  const pathEvent = location.pathname.split('/')[1] || '';
  const activeEvent = params.event || pathEvent || '';

  const activeEventRef = useRef(activeEvent);
  activeEventRef.current = activeEvent;

  const { onChangeColorCustom } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const onChangeColorCustomRef = useRef(onChangeColorCustom);
  onChangeColorCustomRef.current = onChangeColorCustom;

  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;

  const isMountedRef = useRef(false);
  const prevEventRef = useRef(activeEvent);

  const [currentEventRow, setCurrentEventRow] = useState(() => getCachedDataForSlug(activeEvent));
  const [returnedData, setreturnedData] = useState(() => {
    const cached = getCachedDataForSlug(activeEvent);
    return cached?.eventRows?.[0] || null;
  });
  const [error, setError] = useState(false);
  const [expired, setExpired] = useState(() => {
    const cached = getCachedDataForSlug(activeEvent);
    return checkRowExpired(cached);
  });
  const [stillLoading, setstillLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    const requestedEvent = activeEvent;

    if (!isMountedRef.current) {
      isMountedRef.current = true;
      const initialCached = getCachedDataForSlug(requestedEvent);
      if (initialCached?.color) {
        onChangeColorCustomRef.current(initialCached.color);
      }
    } else if (prevEventRef.current !== requestedEvent) {
      prevEventRef.current = requestedEvent;

      // Reset or scope row and state whenever activeEvent changes
      const cached = getCachedDataForSlug(requestedEvent);
      const isExp = checkRowExpired(cached);

      setCurrentEventRow(cached);
      setreturnedData(cached?.eventRows?.[0] || null);
      setError(false);
      setExpired(isExp);

      if (cached?.color) {
        onChangeColorCustomRef.current(cached.color);
      }
    }

    if (!requestedEvent) {
      setstillLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    setstillLoading(true);

    const handleSuccess = (res) => {
      // Reject responses if this request was cancelled or if the active event changed
      if (isCancelled || activeEventRef.current !== requestedEvent) {
        return;
      }

      try {
        if (res?.rows?.[0]) {
          const eventRow = res.rows[0];

          // Persist to localStorage bound strictly to the requested slug
          localStorage.setItem('arsh:event', JSON.stringify(eventRow));
          localStorage.setItem('arsh:activeevent', requestedEvent);

          setCurrentEventRow(eventRow);
          setreturnedData(eventRow?.eventRows?.[0] || null);

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('arsh:event-updated', { detail: eventRow }));
          }

          if (eventRow?.color) {
            onChangeColorCustomRef.current(eventRow.color);
          }

          let isRowExpired = false;
          try {
            const x41 = eventRow?.eventRows?.[0]?.x41;
            if (x41) {
              isRowExpired = checkIfPackageExpired(decrypt(x41))?.isExpired === true;
            }
          } catch (e) {
            console.error('Failed to validate event expiration:', e);
            setError(true);
            setstillLoading(false);
            return;
          }

          setExpired(isRowExpired);
          setError(false);
          setstillLoading(false);
        } else {
          // Empty rows indicates an unknown or invalid event slug
          localStorage.removeItem('arsh:event');
          localStorage.removeItem('arsh:activeevent');
          setError(true);
          setstillLoading(false);
        }
      } catch (err) {
        console.error('Error handling event details response:', err);
        setError(true);
        setstillLoading(false);
      }
    };

    const handleError = (err) => {
      // Reject responses if this request was cancelled or if the active event changed
      if (isCancelled || activeEventRef.current !== requestedEvent) {
        return;
      }
      enqueueSnackbarRef.current(err || 'Failed to load event details', {
        variant: 'error',
      });
      setError(true);
      setstillLoading(false);
    };

    get(`${endpoints?.geteventdetails}?event=${requestedEvent}`, handleSuccess, handleError, false);

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEvent]);

  const ChangeToClientColor = () => {
    try {
      const color = currentEventRow?.color || JSON.parse(localStorage.getItem('arsh:event'))?.color;
      onChangeColorCustom(color || 'blue');
    } catch (e) {
      onChangeColorCustom('blue');
    }
  };

  useEffect(() => {
    if (returnedData) {
      if (checkIfPackageExpired(decrypt(returnedData?.x41))?.isExpired === true) {
        setExpired(true);
      }
    }
  }, [returnedData]);

  return (
    <>
      <HeaderStyle>
        <p id="re3423" onClick={() => ChangeToClientColor()}></p>
      </HeaderStyle>
      {error && <Navigate to="/404" />}
      {stillLoading && blocking ? (
        <LoadingScreen />
      ) : expired ? (
        <EventExpired />
      ) : (
        <Outlet context={{ eventDetails: currentEventRow, activeEvent }} />
      )}
    </>
  );
}
